import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, LayoutGrid } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Club, CLUB_STATUS } from '@/types/club.model';
import { enableClub, disableClub } from '@/api/clubs/clubApi';
import { handleGlobalError } from '@/utils/error-handler';
import { ClubThumbnail } from '@/components/clubs/clubs-list/ClubThumbnail';
import { ClubStat } from '@/components/clubs/clubs-list/ClubStat';
import { StatusPill } from '@/components/clubs/clubs-list/StatusPill';
import { ClubRowMenu } from '@/components/clubs/clubs-list/ClubRowMenu';
import { TransferClubDialog } from '@/components/clubs/TransferClubDialog';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';

interface ClubRowProps {
  club: Club;
  role: 'owner' | 'future_owner' | 'manager' | 'instructor';
  isFirst?: boolean;
  /** A mutation on this club succeeded — the list should refresh. */
  onClubChanged?: () => void;
}

export const ClubRow: React.FC<ClubRowProps> = ({
  club,
  role,
  isFirst = false,
  onClubChanged,
}) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(club.status);
  const [isToggling, setIsToggling] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const isReadOnly = role !== 'owner' && role !== 'future_owner';

  const addressLine = `${club.address.street}, ${club.address.city}, ${club.address.state}`;

  const courtsCount = club.courts.length;
  const managersCount = role === 'owner' ? club.managers.length : null;
  const instructorsCount = club.instructors.length;

  // Handlers
  const onManage = () => {
    navigate(`/clubs/${club.id}/courts${isReadOnly ? '?readonly=true' : ''}`, {
      state: { courts: club.courts, clubId: club.id, isReadOnly },
    });
  };

  const onManagers = () => {
    navigate('/administrar-manager', {
      state: { clubId: club.id, clubName: club.name, clubStatus: status, clubManagers: club.managers },
    });
  };

  const onInstructors = () => {
    navigate('/administrar-instructor', {
      state: {
        clubId: club.id,
        clubName: club.name,
        clubStatus: status,
        clubInstructors: club.instructors,
        // The backend lets any club staff invite instructors, but only the owner remove them.
        canRemoveInstructors: role === 'owner',
      },
    });
  };

  const onEdit = () => {
    navigate('/clubs/create', { state: { club, isReadOnly: false } });
  };

  const onDetails = () => {
    navigate('/clubs/create', { state: { club, isReadOnly: true } });
  };

  const onRanking = () => {
    navigate(`/clubs/${club.id}/ranking`, { state: { clubId: club.id } });
  };

  const onTransfer = () => {
    setTransferOpen(true);
  };

  const onToggleStatus = () => {
    if (status === CLUB_STATUS.ENABLED) {
      setConfirmOpen(true);
    } else {
      doEnable();
    }
  };

  const doDisable = async () => {
    try {
      setIsToggling(true);
      await disableClub(club.id);
      setStatus(CLUB_STATUS.DISABLED);
    } catch (e) {
      handleGlobalError(e, 'Error al deshabilitar el club', 'Ocurrió un error al deshabilitar el club');
    } finally {
      setIsToggling(false);
      setConfirmOpen(false);
    }
  };

  const doEnable = async () => {
    try {
      setIsToggling(true);
      await enableClub(club.id);
      setStatus(CLUB_STATUS.ENABLED);
    } catch (e) {
      handleGlobalError(e, 'Error al habilitar el club', 'Ocurrió un error al habilitar el club');
    } finally {
      setIsToggling(false);
    }
  };

  // Reusable primary button content
  const ManageButtonContent = ({ mobile }: { mobile?: boolean }) => (
    <>
      <LayoutGrid size={14} />
      {mobile ? 'Gestionar canchas' : 'Gestionar'}
    </>
  );

  return (
    <>
      {/* Desktop row */}
      <div
        className={cn(
          'hidden md:flex items-center gap-4 px-[18px] py-[13px] hover:bg-[#FAFBFC] transition-colors',
          !isFirst && 'border-t border-gc-line2',
        )}
      >
        {/* Thumbnail */}
        <ClubThumbnail id={club.id} bannerUrl={club.bannerUrl} name={club.name} size={50} />

        {/* Name + address */}
        <div className="w-[280px] min-w-0">
          <p className="truncate text-[14.5px] font-bold text-gc-ink">{club.name}</p>
          <div className="mt-[3px] flex items-center gap-[5px] text-[12px] text-gc-muted min-w-0">
            <MapPin size={12} className="shrink-0" />
            <span className="truncate">{addressLine}</span>
          </div>
        </div>

        {/* Stats */}
        <div className="ml-1.5 flex gap-[26px]">
          <ClubStat value={courtsCount} label="canchas" />
          <ClubStat value={managersCount} label="managers" />
          <ClubStat value={instructorsCount} label="profesores" />
        </div>

        {/* Right group */}
        <div className="ml-auto flex items-center gap-2.5">
          <StatusPill status={status} size="m" />
          <button
            type="button"
            onClick={onManage}
            disabled={isToggling}
            className="inline-flex items-center justify-center gap-[7px] h-9 px-4 rounded-[10px] bg-gc-navy text-white text-[13.5px] font-semibold whitespace-nowrap hover:bg-gc-navy/90 transition-colors"
          >
            <ManageButtonContent />
          </button>
          <ClubRowMenu
            role={role}
            status={status}
            onManagers={onManagers}
            onInstructors={onInstructors}
            onEdit={onEdit}
            onDetails={onDetails}
            onRanking={onRanking}
            onToggleStatus={onToggleStatus}
            onTransfer={onTransfer}
          />
        </div>
      </div>

      {/* Mobile card */}
      <div className="md:hidden rounded-[14px] border border-gc-line bg-white p-[13px]">
        {/* Header */}
        <div className="flex items-center gap-3">
          <ClubThumbnail id={club.id} bannerUrl={club.bannerUrl} name={club.name} size={46} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[14.5px] font-bold text-gc-ink">{club.name}</p>
            <div className="mt-[3px] flex items-center gap-[5px] text-[12px] text-gc-muted min-w-0">
              <MapPin size={12} className="shrink-0" />
              <span className="truncate">{addressLine}</span>
            </div>
          </div>
          <div className="shrink-0">
            <StatusPill status={status} size="s" />
          </div>
        </div>

        {/* Stats row */}
        <div className="my-[11px] flex gap-[22px] border-y border-gc-line2 py-[11px]">
          <ClubStat value={courtsCount} label="canchas" />
          <ClubStat value={managersCount} label="managers" />
          <ClubStat value={instructorsCount} label="profesores" />
        </div>

        {/* Actions row */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onManage}
            disabled={isToggling}
            className="inline-flex flex-1 w-full items-center justify-center gap-[7px] h-9 px-4 rounded-[10px] bg-gc-navy text-white text-[13.5px] font-semibold whitespace-nowrap hover:bg-gc-navy/90 transition-colors"
          >
            <ManageButtonContent mobile />
          </button>
          <ClubRowMenu
            role={role}
            status={status}
            onManagers={onManagers}
            onInstructors={onInstructors}
            onEdit={onEdit}
            onDetails={onDetails}
            onRanking={onRanking}
            onToggleStatus={onToggleStatus}
            onTransfer={onTransfer}
          />
        </div>
      </div>

      {/* Confirmation modal (shared) */}
      <ConfirmationModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={doDisable}
        title="Deshabilitar club"
        description={`¿Seguro que querés deshabilitar "${club.name}"? Dejará de estar visible para los jugadores.`}
        confirmText="Deshabilitar"
        cancelText="Cancelar"
      />

      {role === 'owner' && (
        <TransferClubDialog
          open={transferOpen}
          onOpenChange={setTransferOpen}
          clubId={club.id}
          clubName={club.name}
          onTransferred={onClubChanged}
        />
      )}
    </>
  );
};
